import { Injectable } from '@angular/core';
import { LiveChatServiceInterface } from './live-chat-service.interface';
import {
  BehaviorSubject,
  Observable,
  of,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { LiveChatMethods } from '../../types/enum';

@Injectable({
  providedIn: 'root',
})
export class GenesysCxSdkMessagingService implements LiveChatServiceInterface {
  private genesys = (<any>window).Genesys;
  private chatId = '';
  private isWebsocketCreated = false;
  private liveChatSubject = new BehaviorSubject<LiveChatResponse>(null);
  liveChatResponse$: Observable<LiveChatResponse> =
    this.liveChatSubject.asObservable();

  createLiveChat(): Observable<any> {
    return of({});
  }

  createLiveChatWebSocket({ chatId = '' }): void {
    this.chatId = chatId;
    this.genesys('subscribe', 'MessagingService.ready', () => {
      if (!this.chatId) {
        this.startConversation();
      }
      this.subscribeToEvents();
    });
  }

  private startConversation(): void {
    this.genesys(
      'command',
      'MessagingService.startConversation',
      {},
      () => {
        /* Websocket created
         * We keep track of the websocket creation in order to be able to
         * disconnect even before receiving the chat-id message
         * */
        this.isWebsocketCreated = true;
        this.liveChatSubject.next(this.onConnectionSuccess());
      },
      () => {
        /*rejected callback*/
      }
    );
  }

  subscribeToEvents(): void {
    this.genesys(
      'subscribe',
      'MessagingService.messagesReceived',
      ({ data }) => {
        this.liveChatSubject.next(this.onReceiveMessages(data.messages[0]));
      }
    );
    this.genesys('subscribe', 'MessagingService.typingReceived', ({ data }) => {
      this.handleOperatorTyping(data.typing.duration);
    });
    this.genesys(
      'subscribe',
      'MessagingService.conversationDisconnected',
      () => {
        this.clearConversation();
      }
    );
    this.genesys('subscribe', 'MessengerHelper.onWebSocketClose', () => {
      this.resetConnection();
    });
  }

  /* sends customer message to operator */
  sendMessage({
    message = '',
    firstName = '',
    lastName = '',
    sessionId = '',
    isFirstConnectionMessage = false,
  }): void {
    let customAttributes = {};
    if (isFirstConnectionMessage) {
      customAttributes = {
        'first-name': firstName,
        'last-name': lastName,
        'phone-number': '',
        email: 'noha.mohamed@rci.rogers.ca',
        product: 'credit-support',
        intent: 'credit-support',
        brand: 'Fido',
        language: 'English',
        'transcript-url': 'https://www.rogers.com',
        'safesend-base-url': 'https://qa01-safesendservice.rogers.com/safesend',
        // 'safesend-base-url': 'http://localhost:4300/',
        'digital-chat-id': sessionId,
        OriginationPageCategory: window.parent.location.pathname,
      };
      this.genesys('command', 'Database.set', {
        messaging: { customAttributes },
      });
    }

    this.genesys('command', 'MessagingService.sendMessage', { message });
  }

  /* sends customer typing indicator to operator */
  setUserTyping(): void {
    this.genesys('command', 'MessagingService.sendTyping');
  }

  /* closes the connection */
  closeConnection() {
    //this.sendMessage({ message: endChatMessage });
    this.clearConversation();
  }

  private onConnectionSuccess(): LiveChatResponse {
    return {
      method: LiveChatMethods.setConnection,
    };
  }

  /* listens to websocket messages */
  private onReceiveMessages(message: any): LiveChatResponse {
    if (message.direction === 'Outbound') {
      if (message.type === 'Text') {
        if (message.originatingEntity === 'Bot') {
          // System message
          return this.onReceiveSystemMessage(message.id, message.text);
        }
        if (message.originatingEntity === 'Human') {
          // Agent message
          return this.onReceiveOperatorMessage(
            message.id,
            message.text,
            message.channel?.from?.nickname,
            ''
          );
        }
      }
    }
  }

  private onReceiveSystemMessage(
    messageId: string,
    message: string
  ): LiveChatResponse {
    if (message.startsWith('$genesys_workflow_response:')) {
      const msg = JSON.parse(message.split('$genesys_workflow_response:')[1]);
      if (msg.conversation_id) {
        this.chatId = msg.conversation_id;
        return {
          method: LiveChatMethods.setChatId,
          value: msg.conversation_id,
        };
      }
      return;
    }
    return {
      method: LiveChatMethods.receiveSystemMessage,
      value: {
        messageId,
        text: message,
      },
    };
  }

  private onReceiveOperatorMessage(
    messageId: string,
    messageText: string,
    operatorName: string,
    operatorId: string
  ): LiveChatResponse {
    return {
      method: LiveChatMethods.receiveOperatorMessage,
      value: {
        messageId,
        text: messageText,
        operatorName,
        operatorId,
      },
    };
  }

  private handleOperatorTyping(duration: number): void {
    timer(duration ? duration : 4000).pipe(
      switchMap(() => of(this.setOperatorTyping(false))),
      startWith(this.setOperatorTyping(true))
    );
  }

  private setOperatorTyping(value: boolean): LiveChatResponse {
    const liveChatResponse = {
      method: LiveChatMethods.setOperatorTyping,
      value,
    };
    this.liveChatSubject.next(liveChatResponse);
    return liveChatResponse;
  }

  private clearConversation(): void {
    /*
     * A websocket may be created, but we haven't received the chatId yet
     * */
    if (this.chatId || this.isWebsocketCreated) {
      this.resetConnection();
      this.genesys('command', 'MessagingService.clearConversation');
    }
  }

  resetConnection(): void {
    if (this.chatId || this.isWebsocketCreated) {
      this.chatId = '';
      this.isWebsocketCreated = false;
      this.liveChatSubject.next({
        method: LiveChatMethods.closeConnection,
      });
    }
  }
}
